import { node, icon } from "~/util/dom";
import DrawTool from "~/report/drawTool";
import { TDrawData } from "./interfaces";
import { _ } from "~/i18n";
import Scene from "~/scene";
import { store } from "~/state/store";

function useCursors(): {
	eraserCursor: () => void;
	drawCursor: () => void;
	cursorOff: () => void;
	} {
	/* eslint-disable no-invalid-this */
	const CURSORS = {
		erase: `url(/img/eraser-cursor.cur) 0 0, crosshair`,
		report: `url(/img/report-cursor.cur) 0 32, crosshair`,
	};

	let cursor = null;

	function cursorOn() {
		if (cursor) {
			return;
		}

		cursor = Mapy.Cursor.add({
			cursor: CURSORS.report,
			callback: this,
		});
	}

	function eraserCursor() {
		if (!cursor) {
			cursorOn();
		}

		Mapy.Cursor.update(cursor, {
			cursor: CURSORS.erase,
			callback: this,
		});
	}

	function drawCursor() {
		if (!cursor) {
			cursorOn();
		}

		Mapy.Cursor.update(cursor, {
			cursor: CURSORS.report,
			callback: this,
		});
	}

	function cursorOff() {
		if (cursor) {
			Mapy.Cursor.remove(cursor);
			cursor = null;
		}
	}

	return {
		eraserCursor,
		drawCursor,
		cursorOff,
	};
}

function createButton(iconName: string, caption: string): HTMLButtonElement {
	const button = node("button", { className: "draw-error-button" });

	button.append(
		icon(iconName),
		node("span", { className: "draw-error-buttonCaption", textContent: caption }),
	);
	button.setAttribute("data-log-ignore", "1");

	return button;
}

function createHUD(): {
	container: HTMLDivElement;
	newDrawButton: HTMLButtonElement;
	eraseDrawButton: HTMLButtonElement;
	drawToMapButton: HTMLButtonElement;
	mapControlButton: HTMLButtonElement;
	} {
	const container = node("div", { className: "draw-error-cont" });
	const newDrawButton = createButton("report-refresh", _("report.draw.newDraw"));
	const eraseDrawButton = createButton("report-erase-rubber", _("reportDefect.form.erase"));
	const drawToMapButton = createButton("report-edit-fill", _("report.draw.drawToMap"));
	const mapControlButton = createButton("report-move-map", _("report.draw.controlMap"));

	container.append(
		newDrawButton,
		eraseDrawButton,
		drawToMapButton,
		mapControlButton,
	);

	return {
		container,
		newDrawButton,
		eraseDrawButton,
		drawToMapButton,
		mapControlButton,
	};
}

type TMakeDrawState = "was-drawed" | "clear";

export function makeDraw(onUpdate?: (state: TMakeDrawState) => void): {
	destroy: () => void;
	getCoords: () => TDrawData;
} {
	const targetLayer = SMap.LAYER_ACTIVE;
	const hideClass = "hide";
	let isErase = false;
	let isMapControl = false;
	const cursors = useCursors();
	const hud = createHUD();

	function wasDrawButtons() {
		hud.newDrawButton.classList.remove(hideClass);
		hud.eraseDrawButton.classList.remove(hideClass);
		hud.drawToMapButton.classList.add(hideClass);
		hud.mapControlButton.classList.remove(hideClass);
	}

	function eraseButtons() {
		hud.newDrawButton.classList.remove(hideClass);
		hud.eraseDrawButton.classList.add(hideClass);
		hud.drawToMapButton.classList.remove(hideClass);
		hud.mapControlButton.classList.remove(hideClass);
	}

	function mapControlButtons() {
		hud.newDrawButton.classList.add(hideClass);
		hud.eraseDrawButton.classList.add(hideClass);
		hud.drawToMapButton.classList.remove(hideClass);
		hud.mapControlButton.classList.add(hideClass);
	}

	const drawUtil = new DrawTool(null, targetLayer, (name: string) => {
		switch (name) {
			case "clear":
				onUpdate && onUpdate("clear");
				break;

			case "draw-start":
				hud.container.classList.add("disable-control");

				if (!isErase && !isMapControl) {
					wasDrawButtons();
				}
				break;
			case "draw-end":
				hud.container.classList.remove("disable-control");
				onUpdate && onUpdate("was-drawed");
				break;

			default:
		}
	});

	function clearErase() {
		if (isErase) {
			const { zoom } = store.getState().map;

			isErase = false;
			drawUtil.endEraseMode(zoom);
		}
	}

	function clearMapControl() {
		if (isMapControl) {
			drawUtil.enableDraw();
			isMapControl = false;
		}
	}

	hud.newDrawButton.addEventListener("click", () => {
		clearErase();
		clearMapControl();
		cursors.drawCursor();
		drawUtil.clear();
	});

	hud.eraseDrawButton.addEventListener("click", () => {
		if (!isErase) {
			isErase = true;
			cursors.eraserCursor();
			drawUtil.startEraseMode();
			eraseButtons();
		}
	});

	hud.drawToMapButton.addEventListener("click", () => {
		clearErase();
		clearMapControl();
		cursors.drawCursor();
		drawUtil.enableDraw();
		wasDrawButtons();
	});

	hud.mapControlButton.addEventListener("click", () => {
		if (!isMapControl) {
			cursors.cursorOff();
			drawUtil.disableDraw();
			mapControlButtons();
			isMapControl = true;
		}
	});

	function destroy() {
		hud.container.remove();
		cursors.cursorOff();
		drawUtil.destroy();
	}

	function getCoords(): TDrawData {
		return drawUtil.getCoords();
	}

	// init
	cursors.drawCursor();
	drawUtil.enableLayer();
	drawUtil.enableDraw();
	wasDrawButtons();
	Scene.getContainer().append(hud.container);

	return {
		destroy,
		getCoords,
	};
}
